import ReactGA from 'react-ga';
import { toastr } from 'react-redux-toastr';
import { takeLatest, put, call } from 'redux-saga/effects';

import * as actions from '../actions';
import { API_URL } from '../../config';
import requext from '../../utils/requext';
import graphFetch from '../../util/graphFetch';
import {
    toastrErrorOptions,
    toastrSuccessOptions,
    toastrWarningOptions,
} from '../../components/Toastr/Toastr';

import {
    DELETE_SITE_REQUEST,
    PROFILE_SITES_SUCCESS,
    PROFILE_SITES_REQUEST,
    CONFIG_DOMAIN_REQUEST,
    DUPLICATE_SITE_REQUEST,
    SAVE_SITE_REQUEST,
    FETCH_SITE_REQUEST,
    EXPORT_FILES_REQUEST,
    EXPORT_JSON_REQUEST,
    UPDATE_USER_SUCCESS,
    EXPORT_FILES_SUCCESS,
    EXPORT_FILES_FAILURE,
} from '../actionTypes';

function* requestSites() {
    const options = {
        queryName: 'profile',
        select: `
        id 
        roles
        createdAt
        isVerified
        totalSites
        publishedSites
        sites {
            id
            url
            name
            status
            domain
            domainType
        }`,
    };

    const { data } = yield call(graphFetch, options);
    if (data.profile) {
        const { sites, ...user } = data.profile;
        if (sites) {
            yield put({
                type: PROFILE_SITES_SUCCESS,
                payload: data.profile.sites,
            });
        }
        if (user) {
            yield put({
                type: UPDATE_USER_SUCCESS,
                payload: { user, message: '' },
            });
        }
    }
}

function* saveSite({ payload }) {
    try {
        const {
            id,
            pages,
            history,
            domainType,
            currentPageId,
            isNotify = true,
            ...rest
        } = payload;
        const options = {
            queryName: 'createOrUpdateSite',
            select: `status success message
            site {
                id url name status domainType domain 
                pages {
                    id name slug type
                }
            }`,
            args: {
                ...rest,
                id: { type: 'ID', value: id },
                pages: { type: '[PageInput]', value: pages },
                domainType: { type: 'DomainTypeEnum', value: domainType },
            },
        };
        const { data } = yield call(graphFetch, options, true);
        if (data?.createOrUpdateSite?.success) {
            const message =
                data.createOrUpdateSite.message ||
                `Successfully ${rest.publish ? 'Published' : 'Saved'}`;
            const { site } = data.createOrUpdateSite;
            isNotify && toastr.success(message, toastrSuccessOptions);
            yield put(actions.saveSiteSuccess({ site, message }));

            // Redirect to newly created page
            const redirectUrl =
                `/builder/site/${site.id}` +
                (currentPageId ? `/page/${currentPageId}` : '');
            history.push(redirectUrl);
        } else {
            const message =
                data?.createOrUpdateSite?.message || 'Something went wrong';
            toastr.error(message, toastrErrorOptions);
            yield put(actions.saveSiteFailure({ id, message }));
            const action = rest.publish ? 'Publish' : 'Save';
            ReactGA.event({ category: 'Site', action, label: 'Fail' });
        }
    } catch (err) {
        console.log(err);
    }
}

function* deleteSite({ siteId }) {
    const query = {
        query: `mutation deleteSite($id: ID!) {
            deleteSite(id: $id) {
                status success message
            }
        }`,
        variables: {
            id: siteId,
        },
    };

    try {
        const resp = yield requext(undefined, JSON.stringify(query));
        const { data } = yield resp.json();
        if (data.deleteSite && data.deleteSite.success) {
            yield put(actions.deleteSiteSuccess(siteId));
            toastr.success(
                'Successfully deleted the site',
                toastrSuccessOptions
            );
        } else {
            yield put(actions.deleteSiteFailure(data.deleteSite.message));
            toastr.error('Failed to delete the site', toastrErrorOptions);
        }
    } catch (err) {
        console.log('ANWARN', err);
    }
}

function* isDomainRegistered({ domain, domainType }) {
    const query = {
        query: `query isDomainRegistered($domain: String!, $domainType: DomainTypeEnum!) {
            isDomainRegistered(domain: $domain, domainType: $domainType)
        }`,
        variables: { domain, domainType },
    };

    try {
        const resp = yield requext(undefined, JSON.stringify(query));
        return yield resp.json();
    } catch (err) {
        console.log('ANWARN');
    }
}

function* configDomain({ payload }) {
    const { publish, id, domain, name, domainType } = payload;
    try {
        const mutation = true;
        const options = {
            queryName: 'createOrUpdateSite',
            args: {
                name,
                domain,
                publish,
                id: { value: id, type: 'ID' },
                domainType: { value: domainType, type: 'DomainTypeEnum' },
            },
            select: `status success message
                    site {
                        id name url status domain domainType
                    }
                `,
        };
        const { data } = yield call(graphFetch, options, mutation);

        if (data.createOrUpdateSite.success) {
            yield put(actions.configDomainSuccess(data.createOrUpdateSite));
        } else {
            yield put(
                actions.configDomainFails(data.createOrUpdateSite.message)
            );
        }
    } catch (err) {
        console.log('ANWARN', err);
    }
}

function* duplicateSite({ siteId: id }) {
    try {
        const query = {
            query: `mutation duplicateSite($id: ID!) {
                duplicateSite(id: $id) {
                    status
                    success
                    message 
                    site {
                        id
                        url
                        data
                        name
                        status
                        domain 
                        domainType
                    }
                }
            }`,
            variables: { id },
        };
        const resp = yield requext(undefined, JSON.stringify(query));
        const { data } = yield resp.json();
        if (data.duplicateSite && data.duplicateSite.success) {
            yield put(actions.duplicateSiteSuccess(id, data.duplicateSite));
        }
    } catch (err) {}
}

function* fetchSite({ siteId, pageId }) {
    const options = {
        queryName: 'site',
        select: `id data global pages {id name slug type}`,
        args: {
            id: { type: 'ID!', value: siteId },
        },
    };

    try {
        const { data } = yield call(graphFetch, options);
        if (data && data.site) {
            yield put(actions.fetchSiteSuccess(data));
            if (pageId) yield put(actions.fetchPage(pageId));
        } else {
            yield put(actions.fetchSiteFailure(data));
        }
    } catch (err) {
        console.warn(err);
    }
}

function* exportFiles(payload) {
    const { siteId } = payload;
    try {
        const options = {
            queryName: 'exportSite',
            select: 'success message url',
            args: { id: { type: 'ID!', value: siteId } },
        };
        const { data } = yield call(graphFetch, options);
        if (data.exportSite?.success) {
            yield put({ type: EXPORT_FILES_SUCCESS });
            const a = document.createElement('a');
            a.setAttribute('href', data.exportSite.url);
            a.setAttribute('style', { display: 'none' });
            a.setAttribute('download', 'website');
            a.click();
            a.remove();
        } else {
            yield put({ type: EXPORT_FILES_FAILURE });
            toastr.error(
                'Ops!',
                'Cannot download your site now!',
                toastrErrorOptions
            );
        }
    } catch (err) {
        yield put({ type: EXPORT_FILES_FAILURE });
        console.log(err);
    }
}

function* exportJSON({ siteId }) {
    try {
        const options = {
            queryName: 'site',
            select: 'id name global pages { data slug name type }',
            args: {
                id: { type: 'ID!', value: siteId },
            },
        };
        const result = yield call(graphFetch, options);
        if (result?.data?.site) {
            let { name, global, pages } = result.data.site;
            global = !global ? null : JSON.parse(global);
            pages = pages.map((page) => {
                if (!page.data) return page;
                return { ...page, data: JSON.parse(page.data) };
            });

            const dataStr =
                'data:text/json;charset=utf-8,' +
                encodeURIComponent(JSON.stringify({ pages, global }));
            const a = document.createElement('a');
            a.setAttribute('href', dataStr);
            a.setAttribute('style', { display: 'none' });
            a.setAttribute('download', `${name || siteId}.json`);
            a.click();
            a.remove();
        }
    } catch (err) {
        console.log('ANWARN', err);
    }
}

export default [
    takeLatest(SAVE_SITE_REQUEST, saveSite),
    takeLatest(FETCH_SITE_REQUEST, fetchSite),
    takeLatest(DELETE_SITE_REQUEST, deleteSite),
    takeLatest(EXPORT_JSON_REQUEST, exportJSON),
    takeLatest(EXPORT_FILES_REQUEST, exportFiles),
    takeLatest(PROFILE_SITES_REQUEST, requestSites),
    takeLatest(CONFIG_DOMAIN_REQUEST, configDomain),
    takeLatest(DUPLICATE_SITE_REQUEST, duplicateSite),
];
